import { api, extractResponseData } from "./base";

/**
 * Service for interacting with the Reading Practice Requests API endpoints
 */
export const readingPracticeRequestsService = {
  getRequestById: async (id) => {
    try {
      const response = await api.get(
        `/platform/requests/reading-practices/${id}`,
      );
      return extractResponseData(response);
    } catch (error) {
      console.error(`Error fetching request with ID ${id}:`, error);
      throw new Error(`Request fetch failed: ${error.message}`);
    }
  },

  retryRequest: async (id) => {
    try {
      const response = await api.post(
        `/platform/requests/reading-practices/${id}/retry`,
      );
      return extractResponseData(response);
    } catch (error) {
      console.error(`Error retrying request with ID ${id}:`, error);
      throw new Error(`Retry failed: ${error.message}`);
    }
  },

  searchRequests: async (searchParams) => {
    try {
      const response = await api.post(
        "/platform/requests/reading-practices/search",
        searchParams,
      );
      const raw = extractResponseData(response);
      return {
        content: raw.items,
        pageNumber: raw.pageNumber,
        pageSize: raw.pageSize,
        totalElements: raw.totalItems,
        totalPages: raw.totalPages,
        hasNext: raw.hasNext,
        hasPrevious: raw.hasPrevious,
      };
    } catch (error) {
      console.error("Error searching reading practice requests:", error);
      throw new Error(`Search failed: ${error.message}`);
    }
  },

  submitPracticeCollectionRequest: async (requestData) => {
    try {
      const response = await api.post(
        "/platform/requests/practice-collections",
        requestData,
      );
      return extractResponseData(response);
    } catch (error) {
      console.error("Error submitting practice collection request:", error);
      throw new Error(`Request submission failed: ${error.message}`);
    }
  },

  getPracticeCollectionRequestById: async (id) => {
    try {
      const response = await api.get(
        `/platform/requests/practice-collections/${id}`,
      );
      return extractResponseData(response);
    } catch (error) {
      console.error(
        `Error fetching practice collection request with ID ${id}:`,
        error,
      );
      throw new Error(`Request fetch failed: ${error.message}`);
    }
  },

  generateCollectionItem: async (requestId, itemId) => {
    try {
      const response = await api.post(
        `/platform/requests/practice-collections/${requestId}/items/${itemId}/generate`,
      );
      return extractResponseData(response);
    } catch (error) {
      console.error(
        `Error generating collection item ${itemId} for request ${requestId}:`,
        error,
      );
      throw new Error(`Item generation failed: ${error.message}`);
    }
  },

  searchPracticeCollectionRequests: async (searchParams) => {
    try {
      const response = await api.post(
        "/platform/requests/practice-collections/search",
        searchParams,
      );
      const raw = extractResponseData(response);
      return {
        items: raw.items || [],
        hasNext: raw.hasNext || false,
      };
    } catch (error) {
      console.error("Error searching practice collection requests:", error);
      throw new Error(`Search failed: ${error.message}`);
    }
  },

  editCollectionItem: async (
    requestId,
    itemId,
    topic,
    plot = null,
    connectionToSource = null,
  ) => {
    try {
      const response = await api.put(
        `/platform/requests/practice-collections/${requestId}/items/${itemId}`,
        {
          topic,
          plot,
          connectionToSource,
        },
      );
      return extractResponseData(response);
    } catch (error) {
      console.error(
        `Error editing collection item ${itemId} for request ${requestId}:`,
        error,
      );
      throw new Error(`Item edit failed: ${error.message}`);
    }
  },

  deleteCollectionItem: async (requestId, itemId) => {
    try {
      const response = await api.delete(
        `/platform/requests/practice-collections/${requestId}/items/${itemId}`,
      );
      return extractResponseData(response);
    } catch (error) {
      console.error(
        `Error deleting collection item ${itemId} for request ${requestId}:`,
        error,
      );
      throw new Error(`Item deletion failed: ${error.message}`);
    }
  },

  addCollectionItem: async (requestId, index, topic) => {
    try {
      const response = await api.post(
        `/platform/requests/practice-collections/${requestId}/items`,
        { index, topic },
      );
      return extractResponseData(response);
    } catch (error) {
      console.error(
        `Error adding collection item for request ${requestId}:`,
        error,
      );
      throw new Error(`Item addition failed: ${error.message}`);
    }
  },

  deletePracticeCollectionRequest: async (id) => {
    try {
      const response = await api.delete(
        `/platform/requests/practice-collections/${id}`,
      );
      return extractResponseData(response);
    } catch (error) {
      console.error(
        `Error deleting practice collection request with ID ${id}:`,
        error,
      );
      throw new Error(`Request deletion failed: ${error.message}`);
    }
  },
};
